import React from 'react';
import { Grid, InputLabel, MenuItem, OutlinedInput, Select } from '@mui/material';
import EditIcon from '@mui/icons-material/Edit';
import DeleteIcon from '@mui/icons-material/Delete';
import { DataGrid, GridColDef } from '@mui/x-data-grid';
import PageHeader from '../../../components/page-header';
import { FilterPanel } from '../../../components/filter-panel';
import { GridPanel, dataGridDefaults } from '../../../components/list-grid-panel';
import { RowAction } from '../../../components/row-action';
import { usePagedList } from '../../../hooks/use-paged-list';
import ParlamentarianAppService from '../../../application/platform/sge/parlamentarian/parlamentarian-app-service';
import { useParlamentarianSearchController } from './context/parlamentarian-search-controller';
import { useParlamentarianSearchState } from './context/parlamentarian-search-context';
import { PARLAMENTARIAN_TYPES, getParlamentarianTypeLabel } from './components/parlamentarian-form-utils';
import { ParlamentarianDrawer } from './components/parlamentarian-drawer';

export function ParlamentarianSearchComponent() {
    const ctrl = useParlamentarianSearchController();
    const { parameters, searchTrigger, politicalParties, drawer } = useParlamentarianSearchState();
    const { rows, rowCount, loading } = usePagedList(ParlamentarianAppService.list, parameters, searchTrigger, 'Não foi possível carregar os parlamentares');

    const columns: GridColDef[] = [
        { field: 'id', headerName: 'ID', width: 80 },
        { field: 'personName', headerName: 'Nome', flex: 1, minWidth: 200 },
        { field: 'type', headerName: 'Tipo', width: 160, valueFormatter: (params) => getParlamentarianTypeLabel(params.value) },
        { field: 'politicalPartyAbbreviation', headerName: 'Partido', width: 100 },
        { field: 'isActive', headerName: 'Ativo', width: 80, type: 'boolean' },
        {
            field: 'actions', headerName: 'Ações', width: 100, sortable: false,
            renderCell: (params) => (
                <React.Fragment>
                    <RowAction title="Editar" onClick={() => ctrl.openDrawer(params.row)}>
                        <EditIcon fontSize="small" />
                    </RowAction>
                    <RowAction title="Excluir" color="error" onClick={() => ctrl.handleDelete(params.row)}>
                        <DeleteIcon fontSize="small" />
                    </RowAction>
                </React.Fragment>
            )
        }
    ];

    return (
        <React.Fragment>
            <Grid item xs={12}>
                <PageHeader
                    title="Parlamentares"
                    subtitle="Gerencie os parlamentares cadastrados"
                    breadcrumbs={[{ label: 'Parlamentares' }]}
                />
            </Grid>
            <Grid item xs={12}>
                <FilterPanel onClear={ctrl.handleClearFilters} onSearch={ctrl.handleSearch}>
                    <Grid item xs={12} md={8}>
                        <InputLabel>Nome</InputLabel>
                        <OutlinedInput fullWidth name="input-name" value={parameters.name || ''} onChange={ctrl.handleOnChange} onKeyDown={(e) => e.key === 'Enter' && ctrl.handleSearch()} />
                    </Grid>
                    <Grid item xs={12} md={4}>
                        <InputLabel>Tipo</InputLabel>
                        <Select fullWidth name="select-type" value={parameters.type ?? ''} onChange={ctrl.handleOnChange}>
                            <MenuItem value=""><em>Todos</em></MenuItem>
                            {PARLAMENTARIAN_TYPES.map((t) => <MenuItem key={t.value} value={t.value}>{t.label}</MenuItem>)}
                        </Select>
                    </Grid>
                </FilterPanel>
            </Grid>
            <Grid item xs={12}>
                <GridPanel addLabel="Novo parlamentar" onAdd={() => ctrl.openDrawer()}>
                    <DataGrid
                        {...dataGridDefaults}
                        rows={rows}
                        columns={columns}
                        loading={loading}
                        rowCount={rowCount}
                        paginationMode="server"
                        paginationModel={{ page: parameters.page ?? 0, pageSize: parameters.pageSize ?? 25 }}
                        onPaginationModelChange={(model) => ctrl.handlePaginationChange(model.page, model.pageSize)}
                    />
                </GridPanel>
            </Grid>

            <ParlamentarianDrawer
                open={drawer.open}
                initial={drawer.record}
                politicalParties={politicalParties}
                isSaving={ctrl.isSaving}
                onClose={ctrl.closeDrawer}
                onSave={ctrl.handleSave}
            />
        </React.Fragment>
    );
}
